import { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

const ADMIN_EMAILS = ['jussi@concept.zone']; // Fallback for backward compatibility

// DF-242: admin = admins/{uid} (Admin SDK only, readable by its owner) or a
// verified auth email on ADMIN_EMAILS. Never users/{uid}.isAdmin -- that doc
// is owner-writable, so trusting it let any user self-grant the admin UI.
export function useAdmin() {
    const { user } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkedUserId, setCheckedUserId] = useState<string | null>(null);

    // Loading is true if we haven't checked this user yet
    const loading = user ? user.uid !== checkedUserId : false;

    useEffect(() => {
        async function checkAdminStatus() {
            if (!user) {
                setIsAdmin(false);
                setCheckedUserId(null);
                return;
            }
            if (user.uid === checkedUserId) return;

            const byEmail = !!user.email && user.emailVerified && ADMIN_EMAILS.includes(user.email);
            try {
                setIsAdmin(byEmail || (await getDoc(doc(db, 'admins', user.uid))).exists());
            } catch (error) {
                console.error('[useAdmin] Error checking admin status:', error);
                setIsAdmin(byEmail);
            } finally {
                setCheckedUserId(user.uid);
            }
        }

        checkAdminStatus();
    }, [user, checkedUserId]);

    return {
        isAdmin,
        loading,
    };
}
